import { useTranslation } from 'react-i18next';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useState, useMemo } from 'react';
import {
  ShiftAssignment,
  User,
  AlgComponentType,
  UserId,
  Section,
  SectionSlot,
} from '@youshift/shared/types';
import {
  returnColor,
  formatUserName,
  dateToString,
} from '@youshift/shared/utils';
import { ComponentPrioritySchema } from '@youshift/shared/hooks/mutations';

import ImportanceInput from './ImportanceInput';
import { isUnbreakable, getImportanceValue, getImportanceLevel } from './utils';
import { IconDisplay } from '../../utils/calendar';
import { useItrContext } from '../../layouts/IterationRootLayout/IterationRootLayout';

function ByUserShiftCard({
  shift_assignment,
  sectionSlotsDict,
  sectionsDict,
  users,
  importanceValues,
  setImportanceValues,
}: {
  shift_assignment: ShiftAssignment;
  sectionSlotsDict: Record<number, SectionSlot>;
  sectionsDict: Record<number, Section>;
  users: Record<number, User>;
  importanceValues: ComponentPrioritySchema;
  setImportanceValues: (
    fn: (prev: ComponentPrioritySchema) => ComponentPrioritySchema,
  ) => void;
}) {
  const { t } = useTranslation();
  const sectionSlot = sectionSlotsDict[shift_assignment.id_section_slot];
  const section = sectionsDict[sectionSlot?.id_section];
  const user = users[shift_assignment.id_user];

  return (
    <div
      key={shift_assignment.id_shift_assignment}
      className="grid grid-cols-[3fr,1fr,2fr] gap-x-4 items-center p-2 hover:bg-gray-50"
    >
      <div className="flex items-center gap-2">
        <div className="flex flex-col">
          <span className="text-sm font-medium flex items-center gap-2">
            {section?.name || t('generic.unknownSection')}
            <span
              className="w-5 h-5 rounded-full"
              style={{ backgroundColor: returnColor(section?.color) }}
            />

            <IconDisplay type={shift_assignment.type} />
          </span>

          <div className="flex flex-col">
            <span className="text-sm text-gray-500">
              {dateToString(sectionSlot?.start, 'weekday-hour')} -{' '}
              {dateToString(sectionSlot?.end, 'weekday-hour')}
            </span>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">
            {t('generic.priority')}
            {': '}
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <ImportanceInput
            value={
              importanceValues[AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS][
                shift_assignment.id_shift_assignment
              ] ?? 0
            }
            onChange={value => {
              const updatedValues: ComponentPrioritySchema = {
                ...importanceValues,
                [AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS]: {
                  ...importanceValues[
                    AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS
                  ],
                  [shift_assignment.id_shift_assignment]: value,
                },
              };
              setImportanceValues(() => updatedValues);
            }}
          />
        </div>
      </div>
    </div>
  );
}

interface PreassingedShiftPriorityByUserProps {
  users: Record<number, User>;
  importanceValues: ComponentPrioritySchema;
  setImportanceValues: (
    fn: (prev: ComponentPrioritySchema) => ComponentPrioritySchema,
  ) => void;
  usersWithPreassignedShifts: Record<number, ShiftAssignment[]>;
}
export default function PreassingedShiftPriorityByUser({
  users,
  importanceValues,
  setImportanceValues,
  usersWithPreassignedShifts,
}: PreassingedShiftPriorityByUserProps) {
  const [expandedUsers, setExpandedUsers] = useState<Set<number>>(new Set());
  const { sectionSlotsDict, sectionsWithSlots } = useItrContext();

  // Create a dictionary of sections for quick lookup
  const sectionsDict: Record<number, Section> = sectionsWithSlots.reduce(
    (acc, { section }) => {
      acc[section.id_section] = section;
      return acc;
    },
    {} as Record<number, Section>,
  );
  const computeUserModeImportance = useMemo(() => {
    const userModes: Record<UserId, number> = {};

    Object.entries(usersWithPreassignedShifts).forEach(([userId, shifts]) => {
      const importanceCounts: Record<number, number> = {};
      let maxCount = 0;
      let mode = 0;

      shifts.forEach(shift => {
        const importance =
          importanceValues[AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS][
            shift.id_shift_assignment
          ] ?? 0;
        importanceCounts[importance] = (importanceCounts[importance] || 0) + 1;

        if (importanceCounts[importance] > maxCount) {
          maxCount = importanceCounts[importance];
          mode = importance;
        }
      });

      userModes[Number(userId)] = mode;
    });

    return userModes;
  }, [usersWithPreassignedShifts, importanceValues]);

  const toggleUser = (userId: number) => {
    setExpandedUsers(prev => {
      const newSet = new Set(prev);
      newSet.has(userId) ? newSet.delete(userId) : newSet.add(userId);
      return newSet;
    });
  };

  // Sort users by name
  const sortedUsersWithPreassignedShifts = Object.entries(
    usersWithPreassignedShifts,
  ).sort(([userIdA], [userIdB]) => {
    const userA = users[Number(userIdA)];
    const userB = users[Number(userIdB)];
    return userA.firstname.localeCompare(userB.firstname);
  });

  return (
    <div>
      <div className="space-y-4">
        {sortedUsersWithPreassignedShifts.map(
          ([userId, userPreassignedShifts]) => {
            const user = users[Number(userId)];
            const isExpanded = expandedUsers.has(Number(userId));
            const userModeImportance =
              computeUserModeImportance[Number(userId)];

            return (
              <div key={userId} className="border rounded">
                <button
                  onClick={() => toggleUser(Number(userId))}
                  className="flex items-center justify-between w-full text-left p-2"
                  style={{
                    borderLeft: `4px solid ${returnColor('blue')}`,
                    backgroundColor: returnColor('blue', 50),
                    color: returnColor('blue', 800),
                  }}
                >
                  <div className="font-medium">
                    {formatUserName(user, false)}
                  </div>
                  <div className="flex items-center gap-2">
                    <ImportanceInput
                      value={userModeImportance}
                      onChange={value => {
                        const updatedValues: ComponentPrioritySchema = {
                          ...importanceValues,
                          [AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS]: {
                            ...importanceValues[
                              AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS
                            ],
                            ...userPreassignedShifts.reduce(
                              (acc, shift) => ({
                                ...acc,
                                [shift.id_shift_assignment]: value,
                              }),
                              {},
                            ),
                          },
                        };
                        setImportanceValues(() => updatedValues);
                      }}
                    />
                    {isExpanded ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                  </div>
                </button>
                {isExpanded && (
                  <div className="p-2 space-y-2">
                    {userPreassignedShifts.map(
                      (shift_assignment: ShiftAssignment) => (
                        <ByUserShiftCard
                          key={shift_assignment.id_shift_assignment}
                          shift_assignment={shift_assignment}
                          sectionSlotsDict={sectionSlotsDict}
                          sectionsDict={sectionsDict}
                          users={users}
                          importanceValues={importanceValues}
                          setImportanceValues={setImportanceValues}
                        />
                      ),
                    )}
                  </div>
                )}
              </div>
            );
          },
        )}
      </div>
    </div>
  );
}
